import React, { Component } from 'react'
import Rheostat from 'rheostat'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import priceRangeSchema from './schema'
import { toFormErrors } from 'react-formal'
import { withCurrency, numberShortFormat, priceLabelWithCutOff } from '../../../../lib/utils/money'
import {debounce, makeId, isDefined, inRange, noop, isNumeric} from '../../../../lib/utils/common'
import PitComponent from './PitComponent'
import Handle from './Handle'
import ErrorMessage from './ErrorMessage'

class PriceInput extends Component {
  static propTypes = {
    priceExtremum: PropTypes.shape({
      priceMin: PropTypes.number.isRequired,
      priceMax: PropTypes.number.isRequired,
      histogramMin: PropTypes.number.isRequired,
      histogramMax: PropTypes.number.isRequired,
      counts: PropTypes.object.isRequired,
      cutOff: PropTypes.number.isRequired
    }).isRequired,
    value: PropTypes.arrayOf(PropTypes.number),
    onChange: PropTypes.func,
    className: PropTypes.string,
    handleTemplate: PropTypes.func
  }

  static defaultProps = {
    handleTemplate: v => withCurrency(numberShortFormat(v)),
    onChange: noop
  }

  constructor(props) {
    super(props)

    this.state = {
      ...this.getInitialState(this.props)(),
      activeInput: null
    }
  }

  getInitialState = ({ value, priceExtremum }) => () => {
    const priceFrom = isDefined(value[0]) ? value[0] : priceExtremum.histogramMin
    const priceTo = isDefined(value[1]) ? value[1] : priceExtremum.histogramMax

    return {
      priceFrom,
      priceTo,
      value: [priceFrom, priceTo],
      errors: {}
    }
  }

  updateValues = ({ values }) => {
    this.setState(this.getInitialState({ value: values, priceExtremum: this.props.priceExtremum }), this.notifyChange)
  }

  debouncedUpdateValues = debounce(this.updateValues, 200)

  notifyChange = () => {
    this.props.onChange(this.state.value)
  }

  validateAndChangePrice = name => event => {
    const value = isNumeric(Number(event.target.value)) ? Number(event.target.value) : 0

    this.setState({ [name]: value }, () => {
      const { priceFrom, priceTo } = this.state

      priceRangeSchema(this.allowedPriceMin, this.allowedPriceMax)
        .validate({ priceFrom, priceTo }, { abortEarly: false })
        .then(() => this.updateValues({ values: [priceFrom, priceTo] }))
        .catch(err => {
          this.setState(state => {
            return { ...state, errors: { ...toFormErrors(err) } }
          })
        })
    })
  }

  setActiveInput = activeInput => e => {
    e.persist()
    this.setState(() => ({ activeInput }), () => {
      e.target.select()
    })
  }

  unsetActiveInput = () => {
    this.setState(() => ({ activeInput: null }))
  }

  isInputActive = name => (
    this.state.activeInput === name
  )

  get allowedPriceMin() {
    const { priceExtremum: { priceMin, histogramMin } } = this.props
    return Math.min(priceMin, histogramMin)
  }

  get allowedPriceMax() {
    const { priceExtremum: { priceMax, histogramMax } } = this.props
    return Math.max(priceMax, histogramMax)
  }

  get pitPoints() {
    return Object.keys(this.props.priceExtremum.counts).map(a => parseInt(a, 10))
  }

  render() {
    const { priceExtremum, className, handleTemplate } = this.props
    const { priceFrom, priceTo, errors, value } = this.state
    const priceInputClass = classNames('PriceInputHome', className)
    const { cutOff, histogramMin, histogramMax } = priceExtremum
    const normalizedValues = [
      inRange(value[0], { from: histogramMin, to: histogramMax }),
      inRange(value[1], { from: histogramMin, to: histogramMax })
    ]
    const priceFromId = makeId()
    const priceToId = makeId()
    const labelValues = normalizedValues.map(val => handleTemplate(val, cutOff))

    return (
      <div className={priceInputClass}>
        <div className='PriceInputHome-diagram'>
          <Rheostat
            className='PriceInputHome-slider'
            min={histogramMin}
            max={histogramMax}
            values={normalizedValues}
            pitComponent={props => (
              <PitComponent {...props} priceExtremum={priceExtremum}/>
            )}
            handle={Handle}
            pitPoints={this.pitPoints}
            snap
            snapPoints={this.pitPoints}
            onValuesUpdated={this.debouncedUpdateValues}
          />
          <div className='PriceInputHome-labelsContainer' role='presentation'>
            <span className='PriceInputHome-label'>{labelValues[0]}</span>
            <span className='PriceInputHome-label'>{labelValues[1]}</span>
          </div>
        </div>
        <div className='PriceInputHome-fields'>
          <div className='PriceInputHome-field'>
            <label htmlFor={priceFromId} className='Label Label--larger'>
              Min Price
            </label>
            <input
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              name='priceFrom'
              id={priceFromId}
              className={classNames('TextField TextField--lightBorders', { isInvalid: errors.priceFrom })}
              value={this.isInputActive('priceFrom') ? priceFrom : priceLabelWithCutOff(priceFrom)}
              onFocus={this.setActiveInput('priceFrom')}
              onBlur={this.unsetActiveInput}
              onChange={this.validateAndChangePrice('priceFrom')}
              aria-invalid={Boolean(errors.priceFrom) || null}
            />

            {errors.priceFrom && (<ErrorMessage ariaDescribedby={priceFromId} errors={errors.priceFrom}/>)}
          </div>

          <div className='PriceInputHome-field'>
            <label htmlFor={priceToId} className='Label Label--larger'>
              Max Price
            </label>
            <input
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              name='priceTo'
              id={priceToId}
              className={classNames('TextField TextField--lightBorders', { isInvalid: errors.priceTo })}
              value={this.isInputActive('priceTo') ? priceTo : priceLabelWithCutOff(priceTo)}
              onFocus={this.setActiveInput('priceTo')}
              onBlur={this.unsetActiveInput}
              onChange={this.validateAndChangePrice('priceTo')}
              aria-invalid={Boolean(errors.priceTo) || null}
            />

            {errors.priceTo && (<ErrorMessage ariaDescribedby={priceToId} errors={errors.priceTo}/>)}
          </div>
        </div>
      </div>
    )
  }
}

export default PriceInput
